import {
  Box,
  Stack,
  alpha,
  darken,
  hexToRgb,
  styled,
  useTheme,
  type Theme,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Typography } from "@platform/design-system";
import { ErrorCode, FileError, FileRejection } from "react-dropzone";

import { FileInfo } from "./census-dropzone-accepted-file";
import { MAX_SIZE } from "./constants";
import { formatBytes } from "./utils";

const ErrorBox = styled(Box)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  backgroundColor: alpha(hexToRgb(theme.palette.dropzone.error), 0.1),
  color: darken(theme.palette.dropzone.error, 0.6),
  cursor: "pointer",
}));

interface CensusDropzoneErrorFileProps {
  rejectedFile: FileRejection;
}

interface IErrorData {
  title: string;
  description: React.ReactNode;
}

const CensusDropzoneErrorFile = ({
  rejectedFile,
}: CensusDropzoneErrorFileProps): React.JSX.Element | null => {
  const theme = useTheme();

  const getErrorData = (error: FileError, fileName?: string) => {
    if (error.code === ErrorCode.FileTooLarge) {
      return {
        title: "File size exceeded",
        description: (
          <Typography variant="body-medium" color="inherit" component="p">
            <Typography variant="body-medium" color="inherit" component="span">
              <strong>{fileName}</strong>
            </Typography>{" "}
            could not be uploaded. File size exceeds the maximum (
            {formatBytes(MAX_SIZE)})
          </Typography>
        ),
      };
    } else if (error.code === ErrorCode.FileInvalidType) {
      return {
        title: "File extension is not supported",
        description: (
          <Typography variant="body-medium" color="inherit" component="p">
            Please make sure you upload a valid
            <Typography variant="body-medium" color="inherit" component="span">
              {" "}
              <strong>.xls</strong>
            </Typography>{" "}
            or{" "}
            <Typography variant="body-medium" color="inherit" component="span">
              <strong>.xlsx</strong>
            </Typography>{" "}
            file.
          </Typography>
        ),
      };
    } else if (error.code === ErrorCode.TooManyFiles) {
      return {
        title: "Uploading multiple files is not supported.",
        description: (
          <Typography variant="body-medium" color="inherit" component="p">
            Please upload only one file.
          </Typography>
        ),
      };
    }
    return null;
  };

  const errorData: IErrorData | null = getErrorData(
    rejectedFile.errors[0],
    rejectedFile.file.name,
  );

  if (!errorData) {
    return null;
  }

  return (
    <ErrorBox mt={3}>
      <ErrorOutlineIcon sx={{ color: theme.palette.dropzone.error }} />
      <FileInfo>
        <Typography color="inherit">
          <strong>{errorData?.title}</strong>
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {errorData?.description}
        </Stack>
      </FileInfo>
    </ErrorBox>
  );
};

export default CensusDropzoneErrorFile;
